import { Auth } from 'aws-amplify';
import API_REST from '../../../api-rest-uruk';
import { translateLiteral } from '../../../i18n/translateHelper';

const FILE_TYPES_ACCEPTED = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.odt', '.ods', '.odp'];
const DEFAULT_SENDER_OPTION = 'uruk_sender';

export const getSchema = (whitelist, signature) => ({
  type: 'object',
  title: translateLiteral('certificate:createForm:create'),
  properties: {
    type: {
      type: 'string',
      title: translateLiteral('certificate:createForm:type:title'),
      enumNames: [translateLiteral('certificate:createForm:type:email'), translateLiteral('certificate:createForm:type:sms')],
      enum: ['email', 'sms']
    },
    senderInfo: {
      type: 'string',
      title: translateLiteral('certificate:createForm:senderInfo'),
      enumNames: [translateLiteral('certificate:createForm:whitelist')].concat(whitelist),
      enum: [DEFAULT_SENDER_OPTION].concat(whitelist),
      default: DEFAULT_SENDER_OPTION
    },
    auditLevel: {
      type: 'string',
      title: translateLiteral('certificate:createForm:audit:level:title'),
      enumNames: [
        translateLiteral('certificate:createForm:audit:level:sent'),
        translateLiteral('certificate:createForm:audit:level:delivered'),
        translateLiteral('certificate:createForm:audit:level:read'),
        translateLiteral('certificate:createForm:audit:level:opened')
      ],
      enum: ['sent', 'delivered', 'read', 'opened']
    }
  },
  required: ['type', 'auditLevel', 'rawMessage'],
  dependencies: {
    type: {
      oneOf: [
        {
          properties: {
            type: {
              enum: ['email']
            },
            emails: {
              type: 'array',
              title: translateLiteral('certificate:createForm:email'),
              minItems: 1,
              items: {
                type: 'string'
              }
            },
            rawSubject: {
              type: 'string',
              title: translateLiteral('certificate:createForm:rawSubject'),
              minLength: 3
            },
            rawMessage: {
              type: 'string',
              title: translateLiteral('certificate:createForm:rawMessage'),
              toolbar: true,
              signature
            },
            files: {
              $ref: '#/$defs/files'
            }
          },
          required: ['emails', 'rawSubject']
        },
        {
          properties: {
            type: {
              enum: ['sms']
            },
            auditLevel: {
              enum: ['sent', 'delivered', 'opened'],
              enumNames: [
                translateLiteral('certificate:createForm:audit:level:sent'),
                translateLiteral('certificate:createForm:audit:level:delivered'),
                translateLiteral('certificate:createForm:audit:level:opened')
              ]
            },
            phoneNumbers: {
              title: translateLiteral('certificate:createForm:phoneNumber'),
              type: 'array',
              minItems: 1,
              items: {
                type: 'string',
                pattern: '^(\\+\\d{7,15})(,\\s*\\+\\d{7,15})*$'
              }
            },
            rawMessage: {
              type: 'string',
              title: translateLiteral('certificate:createForm:rawMessage'),
              toolbar: false
            }
          },
          required: ['phoneNumbers']
        }
      ]
    },
    auditLevel: {
      oneOf: [
        {
          properties: {
            auditLevel: {
              enum: ['opened']
            },
            openAction: {
              type: 'string',
              title: translateLiteral('certificate:createForm:audit:openAction:title'),
              enumNames: [
                translateLiteral('certificate:createForm:audit:openAction:approval'),
                translateLiteral('certificate:createForm:audit:openAction:otp'),
                translateLiteral('certificate:createForm:audit:openAction:signature')
              ],
              enum: ['approval', 'otp', 'signature']
            },
            files: {
              $ref: '#/$defs/files'
            }
          },
          required: ['files', 'openAction']
        },
        {
          properties: {
            auditLevel: {
              enum: ['read']
            },
            type: {
              enum: ['email']
            }
          }
        }
      ]
    }
  },
  $defs: {
    files: {
      type: 'array',
      title: translateLiteral('certificate:createForm:files'),
      items: {
        type: 'object',
        properties: {
          name: {
            type: 'string'
          },
          base64: {
            type: 'string'
          }
        }
      }
    }
  }
});

export const uiSchema = {
  'ui:order': ['type', 'senderInfo', 'auditLevel', 'openAction', 'phoneNumbers', 'emails', 'rawSubject', '*'],
  rawMessage: {
    'ui:field': 'RTEField'
  },
  emails: {
    items: {
      'ui:options': {
        inputType: 'email',
        autocomplete: 'off'
      }
    }
  },
  phoneNumbers: {
    items: {
      'ui:options': {
        inputType: 'tel',
        autocomplete: 'off'
      }
    }
  },
  files: {
    'ui:field': 'UploadFile',
    'ui:options': {
      accept: FILE_TYPES_ACCEPTED.join(','),
      multiple: true
    }
  }
};

export async function createNotification (data) {
  const user = await Auth.currentAuthenticatedUser();
  const currentUserEmail = user.signInUserSession.idToken.payload.email;
  const { type } = data;

  const body = {
    audit: {
      level: data.auditLevel,
      openAction: data.openAction
    },
    metadata: {
      currentUserEmail
    }
  };

  if (type === 'email') {
    const senderInfo = data.senderInfo && data.senderInfo.match(/(?:(?<alias>.+)\s)(?:<(?<email>.+)>)/);
    const senderEmail = (senderInfo && senderInfo.groups && senderInfo.groups.email) || undefined;
    const senderAlias = (senderInfo && senderInfo.groups && senderInfo.groups.alias) || undefined;

    body.emailConfig = {
      subject: data.rawSubject,
      contentType: 'text/html',
      content: data.rawMessage,
      senderEmail,
      senderAlias
    };

    body.recipients = data.emails.map(email => ({ email }));
  } else {
    const senderId = data.senderInfo !== DEFAULT_SENDER_OPTION ? data.senderInfo : undefined;
    body.smsConfig = {
      content: data.rawMessage,
      senderId
    };
    body.recipients = data.phoneNumbers.map(phoneNumber => ({ phoneNumber }));
  }

  if (data.files && data.files.length) {
    body.attachments = data.files.map(file => {
      const { name, base64 } = file;
      return { name, content: base64 };
    });
  }

  await API_REST.createNotification(body);
}

export function getWhitelist (type, appExtraConfig, ctxExtraConfig) {
  const whitelist = [];
  if (type === 'email') {
    return getEmailWhitelist(appExtraConfig, ctxExtraConfig);
  } else if (type === 'email') {
    return getSMSWhitelist(appExtraConfig, ctxExtraConfig);
  }

  return whitelist;
}

function getEmailWhitelist (appExtraConfig, ctxExtraConfig) {
  const whitelist = [];
  if (appExtraConfig.sourceEmailWhitelist) {
    whitelist.push(...transformToStringList(appExtraConfig.sourceEmailWhitelist));
  }
  if (appExtraConfig.fromEmail && appExtraConfig.fromEmailAlias) {
    whitelist.push(`${appExtraConfig.fromEmailAlias} <${appExtraConfig.fromEmail}>`);
  }
  if (ctxExtraConfig.sourceEmailWhitelist) {
    whitelist.push(...transformToStringList(ctxExtraConfig.sourceEmailWhitelist));
  }
  if (ctxExtraConfig.fromEmail && ctxExtraConfig.fromEmailAlias) {
    whitelist.push(`${ctxExtraConfig.fromEmailAlias} <${ctxExtraConfig.fromEmail}>`);
  }

  return whitelist;
}

function getSMSWhitelist (appExtraConfig, ctxExtraConfig) {
  const whitelist = [];
  if (appExtraConfig.senderIdWhitelist) {
    whitelist.push(...appExtraConfig.senderIdWhitelist);
  }
  if (appExtraConfig.senderId) {
    whitelist.push(appExtraConfig.senderId);
  }
  if (ctxExtraConfig.senderIdWhitelist) {
    whitelist.push(...ctxExtraConfig.senderIdWhitelist);
  }
  if (ctxExtraConfig.senderId) {
    whitelist.push(ctxExtraConfig.senderId);
  }

  return whitelist;
}

function transformToStringList (whitelist) {
  return whitelist.map(({ email, alias }) => `${alias} <${email}>`);
}
